import { Role } from '@/Enums/Users/Role.enum';
import { IFilterFormData } from '@/Interfaces/PdfBuilder/FilterFormData.interface';
import { IScheduledPdfCustomReport } from '@/Interfaces/PdfBuilder/ScheduledPdfCustomReport.interface';
import { IScheduleFormData } from '@/Interfaces/ReportBuilder/ScheduleFormData.interface';
import { cancelScheduledPdfReport, schedulePdfReport, updateScheduledPdfReport } from '@/store/api/pdfBuilder.api';
import { useAppSelector } from '@/store/hooks';
import { selectRibeyeAllClients, selectRibeyeAllWhitelabels } from '@/store/selectors/sharedApi.selectors';
import { useCallback } from 'react';
import type { UseFormGetValues } from 'react-hook-form';
import { toast } from 'react-toastify';

interface IUsePdfScheduleActionsParams {
  editingScheduledReport: IScheduledPdfCustomReport | null;
  getValues: UseFormGetValues<IFilterFormData>;
  meRole: Role;
  onAfterSuccess: () => void;
}

export const usePdfScheduleActions = ({
  editingScheduledReport,
  getValues,
  meRole,
  onAfterSuccess,
}: IUsePdfScheduleActionsParams) => {
  const clients = useAppSelector(selectRibeyeAllClients);
  const whitelabels = useAppSelector(selectRibeyeAllWhitelabels);

  const onScheduleSubmit = useCallback(
    async (scheduleFormData: IScheduleFormData) => {
      if (editingScheduledReport) {
        await updateScheduledPdfReport(editingScheduledReport.id, scheduleFormData);
      } else {
        await schedulePdfReport(scheduleFormData, getValues(), meRole, clients, whitelabels);
      }
      onAfterSuccess();
    },
    [editingScheduledReport, getValues, meRole, clients, whitelabels, onAfterSuccess],
  );

  const onCancelScheduled = useCallback(
    async (id: number) => {
      try {
        await cancelScheduledPdfReport(id);
      } catch {
        toast.error('Failed to cancel scheduled report.');
      }
      onAfterSuccess();
    },
    [onAfterSuccess],
  );

  return { onScheduleSubmit, onCancelScheduled };
};
